/**
 * 股息图表数据处理工具函数
 */

/**
 * 月份名称常量
 */
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * 图表数据项接口
 */
export interface ChartDataItem {
  x: string;
  label: string;
}

/**
 * 格式化日期为 "月 日, 年" 格式
 * @param dateStr - 日期字符串
 * @returns 格式化后的日期字符串 (例如: Jun 11, 2025)
 */
export const formatDividendsDate = (dateStr: string): string => {
  if (!dateStr) {
    return "";
  }
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * 格式化金额为美元格式
 * @param amount - 金额数值或字符串
 * @returns 格式化后的金额字符串 (例如: $0.010)
 */
export const formatDividendsNumberData = (
  amount: number | string,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = 4,
): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) {
    return "";
  }
  return `${numAmount.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  })}`;
};

/**
 * 支付频率映射表 - 多语言支持
 */
const PAY_FREQUENCY_MAPS: Record<string, Record<number, string>> = {
  en: {
    0: "Annually",
    1: "Monthly",
    2: "Quarterly",
    3: "Semi-annually",
    4: "Other/Unknown",
    5: "Bimonthly",
    6: "Trimesterly",
    7: "Weekly",
  },
  zh: {
    0: "每年",
    1: "每月",
    2: "每季度",
    3: "每半年",
    4: "其他/未知",
    5: "每两月",
    6: "每三个月",
    7: "每周",
  },
  "zh-tw": {
    0: "每年",
    1: "每月",
    2: "每季度",
    3: "每半年",
    4: "其他/未知",
    5: "每兩月",
    6: "每三個月",
    7: "每週",
  },
  ko: {
    0: "연간",
    1: "월간",
    2: "분기별",
    3: "반기별",
    4: "기타/알 수 없음",
    5: "격월",
    6: "3개월마다",
    7: "주간",
  },
  vi: {
    0: "Hàng năm",
    1: "Hàng tháng",
    2: "Hàng quý",
    3: "Nửa năm",
    4: "Khác/Không xác định",
    5: "Hai tháng một lần",
    6: "Ba tháng một lần",
    7: "Hàng tuần",
  },
  es: {
    0: "Anualmente",
    1: "Mensualmente",
    2: "Trimestralmente",
    3: "Semestralmente",
    4: "Otro/Desconocido",
    5: "Bimensualmente",
    6: "Trimestralmente",
    7: "Semanalmente",
  },
  tr: {
    0: "Yıllık",
    1: "Aylık",
    2: "Üç ayda bir",
    3: "Altı ayda bir",
    4: "Diğer/Bilinmiyor",
    5: "İki ayda bir",
    6: "Üç ayda bir",
    7: "Haftalık",
  },
  fr: {
    0: "Annuellement",
    1: "Mensuellement",
    2: "Trimestriellement",
    3: "Semestriellement",
    4: "Autre/Inconnu",
    5: "Bimestriellement",
    6: "Trimestriellement",
    7: "Hebdomadairement",
  },
  ja: {
    0: "年次",
    1: "月次",
    2: "四半期ごと",
    3: "半年ごと",
    4: "その他/不明",
    5: "隔月",
    6: "四半期ごと",
    7: "週次",
  },
};

/**
 * 格式化支付频率 - 支持多语言
 * @param payFreq - 支付频率数值
 * @param locale - 语言代码 (zh, zh-tw, ko, vi, es, tr, fr, ja, en)
 * @returns 格式化后的支付频率文本
 * @example
 * formatPayFrequency(0, "en") // "Annually"
 * formatPayFrequency(2, "zh") // "每季度"
 * formatPayFrequency(2) // "Quarterly" (默认英语)
 */
export const formatPayFrequency = (
  payFreq: number | string | undefined,
  locale?: string,
): string => {
  if (payFreq === undefined || payFreq === null || payFreq === "") {
    return "";
  }

  const numPayFreq =
    typeof payFreq === "string" ? parseInt(payFreq, 10) : payFreq;

  if (isNaN(numPayFreq)) {
    return "";
  }

  // 默认使用英语，或根据 locale 选择对应的映射表
  const currentLocale = locale && locale !== "en" ? locale : "en";
  const frequencyMap =
    PAY_FREQUENCY_MAPS[currentLocale] || PAY_FREQUENCY_MAPS.en;

  return frequencyMap[numPayFreq] || "";
};

/**
 * 生成均匀分布的X轴刻度
 * 规则：每年最多显示2个刻度，选择上半年和下半年最具代表性的月份
 * @param xAxisData - X轴数据数组
 * @returns 刻度数组
 */
const generateEvenlySpacedTicks = (
  xAxisData: ChartDataItem[],
): Array<{ x: string; label: string }> => {
  const ticks: Array<{ x: string; label: string }> = [];

  if (xAxisData.length === 0) {
    return ticks;
  }

  // 获取起始和结束日期
  const startDate = new Date(xAxisData[0].x);
  const endDate = new Date(xAxisData[xAxisData.length - 1].x);
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // 为每年生成刻度
  for (let year = startYear; year <= endYear; year++) {
    // 查找该年所有数据点
    const yearData = xAxisData.filter((item) => {
      const date = new Date(item.x);
      return date.getFullYear() === year;
    });

    if (yearData.length === 0) {
      continue;
    }

    // 按上下半年分组
    const firstHalfData = yearData.filter((item) => {
      const month = new Date(item.x).getMonth() + 1;
      return month >= 1 && month <= 6;
    });

    const secondHalfData = yearData.filter((item) => {
      const month = new Date(item.x).getMonth() + 1;
      return month >= 7 && month <= 12;
    });

    // 上半年：选择第一个数据点，使用其实际月份
    if (firstHalfData.length > 0) {
      const firstDataPoint = firstHalfData[0];
      const month = new Date(firstDataPoint.x).getMonth(); // 0-based
      ticks.push({
        x: firstDataPoint.x,
        label: `${MONTH_NAMES[month]},${year}`,
      });
    }

    // 下半年：选择第一个数据点，使用其实际月份
    if (secondHalfData.length > 0) {
      const firstDataPoint = secondHalfData[0];
      const month = new Date(firstDataPoint.x).getMonth(); // 0-based
      ticks.push({
        x: firstDataPoint.x,
        label: `${MONTH_NAMES[month]},${year}`,
      });
    }
  }

  return ticks;
};

/**
 * 生成并处理X轴刻度（去除首尾刻度）
 * @param xAxisData - X轴数据数组
 * @returns 处理后的刻度数组（已删除第一个和最后一个刻度）
 */
export const generateProcessedTicks = (
  xAxisData: ChartDataItem[],
): Array<{ x: string; label: string }> => {
  const allTicks = generateEvenlySpacedTicks(xAxisData);
  // 删除第一个和最后一个点，避免离边距太近
  return allTicks.slice(1, -1);
};

/**
 * 生成移动端年度刻度（只显示3个年份，均匀分布在x轴上）
 * @param xAxisData - X轴数据数组
 * @returns 年度刻度数组
 */
export const generateMobileYearlyTicks = (
  xAxisData: ChartDataItem[],
): Array<{ x: string; label: string }> => {
  if (xAxisData.length === 0) {
    return [];
  }

  const dataLength = xAxisData.length;

  // 如果数据点少于等于3个，返回所有数据点
  if (dataLength <= 3) {
    return xAxisData.map((item) => ({
      x: item.x,
      label: new Date(item.x).getFullYear().toString(),
    }));
  }

  // 选择均匀分布的3个数据点（起始、中间、结束）
  const indices = [
    0, // 第一个点
    Math.floor(dataLength / 2), // 中间点
    dataLength - 1, // 最后一个点
  ];

  const ticks: Array<{ x: string; label: string }> = indices.map((index) => {
    const dataPoint = xAxisData[index];
    const year = new Date(dataPoint.x).getFullYear();
    return {
      x: dataPoint.x,
      label: year.toString(),
    };
  });

  return ticks;
};
